import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Dialog } from 'src/components/Dialog';
import { Typography } from 'src/components/Typography';
import { styled } from 'src/components/muiTheme/utils';
import {
  Leaderboard,
  LeaderboardResultItem,
} from 'src/models/leaderboard/_types';
import { formatFullDateAndTime } from 'src/utils/dateFormatter';

import { LeaderboardResultRow } from './LeaderboardResultRow';

interface LeaderboardResultsListProps {
  results: LeaderboardResultItem[];
  tvMode?: boolean;
  numeric?: boolean;
  label?: string;
  leaderboard?: Leaderboard | null;
}

const ListContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.vars.palette.background.paper,
  border: `1px solid ${theme.vars.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

function addTieMarkers(
  results: LeaderboardResultItem[]
): LeaderboardResultItem[] {
  const withTies = results.map((r) => ({ ...r }));
  let currentRank: number | string = 1;

  withTies.forEach((result, index) => {
    const prev = withTies[index - 1];
    const next = withTies[index + 1];

    if (next && next.value === result.value) {
      if (!prev || prev.value !== result.value) {
        currentRank = result.rank;
      }
      result.rank = `${currentRank}T`;
    }

    if (prev && prev.value === result.value) {
      result.rank = `${currentRank}T`;
    }
  });

  return withTies;
}

export const LeaderboardResultsList = ({
  results,
  tvMode = false,
  numeric = true,
  label,
  leaderboard,
}: LeaderboardResultsListProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'leaderboard.public',
  });
  const [selectedResult, setSelectedResult] =
    useState<LeaderboardResultItem | null>(null);

  const resultsWithTies = addTieMarkers(results);

  const showScore = leaderboard?.scoresAvailableOn
    ? new Date(leaderboard.scoresAvailableOn) <= new Date()
    : true;

  return (
    <>
      <ListContainer>
        {resultsWithTies.length > 0 ? (
          resultsWithTies.map((result) => (
            <LeaderboardResultRow
              key={`${result.id}-${result.rank}`}
              result={result}
              tvMode={tvMode}
              numeric={numeric}
              showScore={showScore}
              onClick={setSelectedResult}
            />
          ))
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography>{t('noResults')}</Typography>
          </Box>
        )}
      </ListContainer>

      <Dialog
        open={!!selectedResult}
        onClose={() => setSelectedResult(null)}
        maxWidth="sm"
        fullWidth
      >
        <Box>
          {selectedResult && (
            <Box>
              {selectedResult.entryVideoUrl && (
                <Box
                  component="iframe"
                  src={selectedResult.entryVideoUrl}
                  sx={{ width: '100%', height: 280, border: 0, mb: 2 }}
                  allowFullScreen
                />
              )}
              {!selectedResult.entryVideoUrl &&
                selectedResult.entryThumbnail && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={selectedResult.entryThumbnail}
                      sx={{ maxHeight: 200 }}
                    />
                  </Box>
                )}
              {label && (
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {label}
                </Typography>
              )}
              <LeaderboardResultRow
                result={selectedResult}
                showRank={false}
                numeric={numeric}
                showScore={showScore}
              />
              <Typography sx={{ my: 2 }} color="text.secondary">
                {selectedResult.date &&
                  formatFullDateAndTime(new Date(selectedResult.date * 1000))}
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  );
};
